// Knowing that a new version is ready, and applying it when asked.
//
// What this replaces: index.js used to answer the service worker's "a new build
// is waiting" by immediately telling it to take over, which fires
// `controllerchange`, which reloads the page. So a deploy could reload the tab
// out from under someone -- mid-checkout, mid-address-edit, mid-anything. The
// update always won, and the person using the app was never asked.
//
// Now the waiting worker is held here and the app offers it. Nothing reloads
// until someone chooses to. The cost of that choice is real and worth stating:
// a person who never presses Update stays on the old build, which is why the
// offer comes back on every launch rather than being dismissible for good.
//
// The reload itself still happens the old way -- applyUpdate() posts
// SKIP_WAITING, the new worker activates, `controllerchange` fires, index.js
// reloads. This module only decides WHEN that starts.

let waitingRegistration = null;
const listeners = new Set();

const notify = () => {
  for (const fn of listeners) {
    try { fn(Boolean(waitingRegistration)); } catch { /* a bad listener must not block the rest */ }
  }
};

// Called by index.js from the service worker's onUpdate.
export function setWaitingRegistration(registration) {
  waitingRegistration = registration || null;
  notify();
}

export const isUpdateReady = () => Boolean(waitingRegistration);

export function subscribeToUpdates(fn) {
  listeners.add(fn);
  // Fire immediately: a subscriber mounting after the worker was found would
  // otherwise never hear about an update that is already waiting.
  try { fn(Boolean(waitingRegistration)); } catch { /* ignore */ }
  return () => listeners.delete(fn);
}

// Hands over to the new build. The page reloads on `controllerchange`.
//
// `registration.waiting` is re-read rather than captured, because between the
// update being found and someone pressing the button the worker may have moved
// on -- another tab could have applied it already.
let reloading = false;

// One reload, whichever path asks for it first.
function reloadOnce() {
  if (reloading) return;
  reloading = true;
  window.location.reload();
}

export function applyUpdate() {
  const reg = waitingRegistration;
  if (!reg) return false;
  const worker = reg.waiting || reg.installing;
  if (!worker) {
    // Nothing left to activate: the update already went through elsewhere.
    // Reload so this tab is not the only one left on the old build.
    reloadOnce();
    return true;
  }
  // The reload is armed HERE rather than left to the global handler in
  // installControllerChangeReload(), because that one deliberately ignores the
  // first install -- and an update applied during the same page life as the
  // install looks exactly like a first install to it. Measured: the banner
  // appeared, Update was pressed, the new worker activated, and nothing
  // reloaded. Arming it on the press ties the reload to the request instead of
  // to how the page happened to start.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', reloadOnce, { once: true });
  }
  worker.postMessage({ type: 'SKIP_WAITING' });
  return true;
}

// Reloads when a worker takes over that this page did not ask for -- an update
// applied in another tab, which leaves this one running the old build.
//
// Skips the first install: clientsClaim() in service-worker.js fires
// `controllerchange` when it takes over a page that had no controller, and
// there is nothing newer to reload onto -- the page already IS the build that
// just installed. Reloading there threw away a first-time visitor's page for
// nothing, which is what it looked like: a spontaneous refresh seconds after
// arriving.
export function installControllerChangeReload() {
  if (!('serviceWorker' in navigator)) return;
  const hadController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return;
    reloadOnce();
  });
}

// Asks the browser to look for a new build now.
//
// A service worker is normally only re-checked on navigation, so a PWA left
// open on one screen for hours would not notice a deploy. This is called when
// the app is brought back to the foreground, and on a slow timer, so "how do I
// get updates" has an answer that does not require closing the app.
export async function checkForUpdate() {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    await reg.update();
    // onUpdate fires on its own if this found something; if a worker was
    // already sitting in `waiting` when we looked, nothing would have fired, so
    // pick that up here too.
    if (reg.waiting) setWaitingRegistration(reg);
    return Boolean(reg.waiting);
  } catch {
    // Offline, or the browser declined. Nothing to report.
    return false;
  }
}

const HOUR = 60 * 60 * 1000;

// Starts the background checks. Called once, from index.js.
export function watchForUpdates({ intervalMs = 6 * HOUR } = {}) {
  if (!('serviceWorker' in navigator)) return () => {};

  const onVisible = () => {
    if (document.visibilityState === 'visible') checkForUpdate();
  };
  document.addEventListener('visibilitychange', onVisible);

  // Deliberately slow. This is a shop, not a chat client: checking every few
  // hours catches a deploy the same day without a request the user is paying
  // for on a mobile connection.
  const timer = setInterval(checkForUpdate, intervalMs);

  // And once at startup, for a PWA resumed from the home screen.
  checkForUpdate();

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
  };
}
